import React, { memo, useState } from 'react';
import CancelIcon from '@mui/icons-material/Cancel';
import { Chip, Theme, Paper } from '@mui/material';
import { makeStyles } from '@mui/styles';
import { chipTypes, fieldTypes } from '@/helpers/constants';
import styles from './ArrayChips.module.scss';
import ConfirmDialog from '../Dialog/ConfirmDialog';
import UniversalChip from '../UniversalChip/UniversalChip';
import HighlightText from '../../HighlightText/HighlightText';

const useStyles = makeStyles((theme: Theme) => ({
  root: {
    alignItems: 'center',
    backgroundColor: 'transparent',
    boxShadow: 'none',
    color: 'inherit',
    columnGap: theme.spacing(1),
    display: 'flex',
    listStyle: 'none',
    margin: '0',
  },
  chipRoot: {
    gap: theme.spacing(1),
    display: 'flex',
    flexWrap: 'wrap',
    justifyContent: 'start',
  },
}));

type ArrayChipLogicProps = {
  id: string;
  chipData: string[];
  handleDelete?: (chip: string) => void;
  disabled?: boolean;
  maxChip?: number;
  onOpenDetails?: () => void;
  detailTitle?: string;
  fieldPath?: string[];
};

const ArrayChipLogic: React.FC<ArrayChipLogicProps> = (props) => {
  const { id, chipData, handleDelete, disabled, maxChip = 5, onOpenDetails, detailTitle, fieldPath = [] } = props;
  const [openChipDetails, setOpenChipDetails] = useState(false);
  const classes = useStyles();
  const moreChips = chipData?.length > maxChip ? chipData?.length - maxChip : 0;

  const handleOpenDetails = () => {
    if (onOpenDetails) {
      onOpenDetails();
    } else {
      setOpenChipDetails(true);
    }
  };

  const getIndexedFieldPath = (index) => {
    const fiedPathString = fieldPath.join('.');
    const indexedFieldPath = `${fiedPathString}[${index}]`;

    return indexedFieldPath.split('.');
  };

  return (
    <Paper component="ul" className={classes.root}>
      <div className={classes.chipRoot}>
        {
          handleDelete
            ? (chipData?.slice(0, maxChip).map((chip, index) => (
              <li key={chip}>
                <Chip
                  id={`${id}[${index}]`}
                  disabled={disabled}
                  size="small"
                  label={(
                    <HighlightText
                      fieldId={`${id}[${index}]`}
                      fieldPath={getIndexedFieldPath(index)}
                      fieldType={fieldTypes.CHIP}
                    >
                      {chip}
                    </HighlightText>
                  )}
                  deleteIcon={(
                    <CancelIcon onMouseDown={(event) => {
                      event.stopPropagation();
                      handleDelete(chip);
                    }}
                    />
                  )}
                  onDelete={() => handleDelete(chip)}
                  onMouseDown={(ev) => ev.stopPropagation()}
                />
              </li>
            )))
            : (
              <UniversalChip
                fieldPath={fieldPath}
                chipType={chipTypes.READONLY}
                arrayStrings={chipData?.slice(0, maxChip)}
              />
            )
        }
      </div>
      {moreChips > 0 && (
        <Chip
          disabled={disabled}
          size="small"
          label={`+ ${moreChips}`}
          onClick={handleOpenDetails}
          onMouseDown={(ev) => ev.stopPropagation()}
        />
      )}

      {openChipDetails && (
        <ConfirmDialog
          handleMouseDown={(ev: React.MouseEvent) => ev.stopPropagation()}
          reject={() => setOpenChipDetails(false)}
          title={detailTitle}
        >
          <div className={styles.DialogChipContainer}>
            <UniversalChip
              fieldPath={fieldPath}
              chipType={chipTypes.READONLY}
              arrayStrings={chipData}
            />
          </div>
        </ConfirmDialog>
      )}
    </Paper>
  );
};

export default memo(ArrayChipLogic, (prev, next) => (JSON.stringify(prev) === JSON.stringify(next)));
